import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CustomTextField from "../components/CustomTextField";
import CustomDropdown from "../components/CustomDropdown";
import CustomButton from "../components/CustomButton";
import "../styles/layout.css";
import { toast } from "react-toastify";
import { useOrganizationApi } from "../api/organization";
import { useUsersApi } from "../api//users";
import { useDirectCustomerApi } from "../api/direct_customers";
import { decodeJwt } from "../util/decodeJwt";
import { validatePassword, validatePhoneNumber } from "../util/validation";
import LogoCropper from "./LogoCropper";

export default function UserCreateOrUpdateWidget({ userId, onSubmit, onCancel, isDirectCustomer = false }) {
  const { getVendorUserById, updateVendorUser, registerVendorUser } = useUsersApi();
  const { getDirectCustomerById, updateDirectCustomer } = useDirectCustomerApi();
  const { updateOrgBranding, uploadOrgLogo } = useOrganizationApi();
  const isEditMode = Boolean(userId);
  const id = userId;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    password: "",
    role: "",
    address: "",
    subdomain: "",
    authKey: "",
    templateId: "",
  });

  const [brandData, setBrandData] = useState({ primary_color: "#006337", logo: "" });
  const [logoPreview, setLogoPreview] = useState("");
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [logoModified, setLogoModified] = useState(false);
  const [orgId, setOrgId] = useState("");

  const [loading, setLoading] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");

  // Decode JWT to get current user role
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = decodeJwt(token);
        if (decoded?.role) setCurrentUserRole(decoded.role.toLowerCase());
      } catch (err) {
        console.error("JWT decode failed", err);
      }
    }
  }, []);

  // Role dropdown options based on logged-in user role
  const getRoleOptions = () => {
    switch (currentUserRole) {
      case "admin":
        return [{ value: "manufacturer", label: "Manufacturer" }];
      case "manufacturer":
        return [{ value: "distributor", label: "Distributor" }];
      case "distributor":
        return [{ value: "dealer", label: "Dealer" }];
      case "dealer":
        return [{ value: "customer", label: "Customer" }];
      default:
        return [];
    }
  };

  // Auto-select Role if only one option
  useEffect(() => {
    const roles = getRoleOptions();
    if (roles.length === 1 && !formData.role) {
      setFormData((prev) => ({ ...prev, role: roles[0].value }));
    }
  }, [currentUserRole]);

  // Load user data if editing
  useEffect(() => {
    async function loadUser() {
      if (!isEditMode) return;
      try {
        setLoading(true);
        let user;
        if (isDirectCustomer) {
          const res = await getDirectCustomerById(id);
          user = res?.data || res;
        } else {
          user = await getVendorUserById(id);
        }
        setFormData({
          name: user.name || "",
          phoneNumber: user.phoneNumber || "",
          password: "",
          role: user.role || "",
          address: user.address || user.organization?.address || "",
          subdomain: user.organization?.subdomain || "",
          authKey: user.organization?.authKey || "",
          templateId: user.organization?.templateId || "",
        });
        // Load branding from org
        const orgData = user.organization || {};
        setOrgId(orgData._id || user.organizationId || "");
        setBrandData({
          primary_color: orgData.primary_color || "#006337",
          logo: orgData.logo || "",
        });
        setLogoPreview(orgData.logo || "");
      } catch (err) {
        console.error(err);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [id, isEditMode, isDirectCustomer]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleBrandChange = (key, value) => {
    setBrandData((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!formData.name?.trim()) { toast.error("Name is required"); return false; }
    if (!formData.phoneNumber?.trim()) { toast.error("Phone number is required"); return false; }
    if (!validatePhoneNumber(formData.phoneNumber)) { toast.error("Phone number must be 10 digits"); return false; }
    if (!isEditMode && !formData.password?.trim()) { toast.error("Password is required"); return false; }
    if (formData.password?.trim() && !validatePassword(formData.password)) {
      toast.error("Password must contain 8+ characters, 1 uppercase, 1 lowercase, 1 number, 1 symbol");
      return false;
    }
    if (!isDirectCustomer && !formData.role) { toast.error("Role is required"); return false; }
    if (!formData.address?.trim()) { toast.error("Address is required"); return false; }
    if (!isDirectCustomer && formData.role === "manufacturer" && !formData.subdomain?.trim()) {
      toast.error("Subdomain is required for Manufacturer");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      let result;
      if (isEditMode) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        if (isDirectCustomer) {
          result = await updateDirectCustomer(id, payload);
          toast.success("Customer updated successfully!");
        } else {
          result = await updateVendorUser(id, payload);
          toast.success("User updated successfully!");
        }
      } else {
        result = await registerVendorUser(formData);
        toast.success("User registered successfully!");
      }

      // Save branding for manufacturer — isolated so it never blocks navigation
      const targetOrgId = orgId || result?.data?.organizationId;
      if (formData.role === "manufacturer" && targetOrgId) {
        try {
          let logoValue = brandData.logo;
          if (logoModified && logoValue && logoValue.startsWith("data:")) {
            try {
              const uploadedUrl = await uploadOrgLogo(targetOrgId, logoValue);
              logoValue = uploadedUrl || logoValue;
            } catch (uploadErr) {
              console.warn("Logo upload failed, storing base64 directly:", uploadErr);
            }
          }
          // Cache locally FIRST
          if (logoValue) localStorage.setItem("orgLogo", logoValue);

          await updateOrgBranding(targetOrgId, {
            primary_color: brandData.primary_color,
            logo: logoValue,
          });
        } catch (brandErr) {
          console.warn("Branding save failed (backend):", brandErr);
        }
      }

      if (onSubmit) onSubmit(result?.data || formData);
      else navigate("/users");
    } catch (err) {
      toast.error(err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-container">
      <div className="modal-header">
        {isEditMode ? "Edit User" : "Create User"}
      </div>
      <div className="user-form-card">
        <div className="user-columns">
          <div className="user-column-left">
            <CustomTextField label="Name" value={formData.name} onChange={(e) => handleChange("name", e.target.value)} />
            <CustomTextField label="Phone Number" value={formData.phoneNumber} onChange={(e) => handleChange("phoneNumber", e.target.value)} />
            <CustomTextField label="Password" type="password" value={formData.password} onChange={(e) => handleChange("password", e.target.value)} />
          </div>

          <div className="user-column-right">
            {!isDirectCustomer && (
              <CustomDropdown
                label="Role"
                options={getRoleOptions()}
                value={formData.role}
                onChange={(value) => handleChange("role", value)}
                isMultiSelect={false}
              />
            )}
            <CustomTextField label="Address" value={formData.address} onChange={(e) => handleChange("address", e.target.value)} />
          </div>
        </div>

        {formData.role === "manufacturer" && (
          <div className="manufacturer-config-section">
            <div className="manufacturer-config-divider">Domain Configuration</div>
            <div className="config-group">
              <div className="subdomain-wrapper">
                <CustomTextField
                  label="Sub domain *"
                  value={formData.subdomain}
                  onChange={(e) =>
                    handleChange("subdomain", e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))
                  }
                  disabled={isEditMode}
                />
                {formData.subdomain && (
                  <div className="domain-preview">
                    https://{formData.subdomain}.bcuai.in
                  </div>
                )}
              </div>

              {/* Color Picker */}
              <div className="color-picker-wrapper">
                <label className="color-picker-label">Primary Color</label>
                <div className="color-picker-row">
                  <input
                    type="color"
                    value={brandData.primary_color}
                    onChange={(e) => handleBrandChange("primary_color", e.target.value)}
                    className="color-picker-input"
                    title="Pick brand primary color"
                  />
                  <span className="color-picker-value">{brandData.primary_color}</span>
                </div>
              </div>

              {/* Logo Upload */}
              <div className="logo-upload-wrapper">
                <label className="color-picker-label">Brand Logo</label>
                <div className="logo-upload-row">
                  {logoPreview ? (
                    <div className="logo-preview-box">
                      <img src={logoPreview} alt="Logo" className="logo-preview-img" />
                      <button
                        type="button"
                        className="logo-remove-btn"
                        onClick={() => { setLogoPreview(""); handleBrandChange("logo", ""); }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="logo-upload-btn"
                      onClick={() => setShowLogoCropper(true)}
                    >
                      Upload Logo
                    </button>
                  )}
                </div>
              </div>

              {showLogoCropper && (
                <LogoCropper
                  onDone={(base64) => {
                    setLogoPreview(base64);
                    handleBrandChange("logo", base64);
                    setLogoModified(true);
                    setShowLogoCropper(false);
                  }}
                  onCancel={() => setShowLogoCropper(false)}
                />
              )}
            </div>

            <div className="manufacturer-config-divider">SMS Configuration</div>
            <div className="config-group">
              <CustomTextField
                label="SMS Auth Key"
                value={formData.authKey}
                onChange={(e) => handleChange("authKey", e.target.value)}
              />
              <CustomTextField
                label="SMS Template ID"
                value={formData.templateId}
                onChange={(e) => handleChange("templateId", e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="user-buttons">
          <CustomButton
            label="Cancel"
            className="user-cancel"
            onClick={() => { if (onCancel) onCancel(); else navigate("/users"); }}
          />
          <CustomButton
            label={loading ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update" : "Create"}
            className="user-create"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </div>
    </div>
  );
}
